// Hard Hat Staffing site config. PDF URLs point at the enroll app's
// public/enroll-assets/ folder (stable, unhashed paths, only linked from the
// enroll app itself); the logo is an inlined data URL so it renders from any
// origin (see logoData.ts).
import type {GroupConfig} from '../types';
import {HOW_TO_ENROLL_NO_CODE} from '../defaults/howToEnroll';
import {HARDHAT_LOGO_DATA_URL} from './logoData';

const hardhatLogo = HARDHAT_LOGO_DATA_URL;
const hardhatMedicalSbc = '/enroll-assets/hardhat-medical-benefits-summary.pdf';
const hardhatAxisSbc = '/enroll-assets/hardhat-axis-benefits-summary.pdf';

export const hardhatConfig: GroupConfig = {
  slug: 'hardhat',
  name: 'Hard Hat Staffing',
  logoUrl: hardhatLogo,
  landingVideoUrl: 'https://player.mux.com/PrRkTX4O00rnlKyTfR00szsANWC1MVkxIbPv7ps77NvUc',
  primaryColor: '#1F3A5F',
  heroHeadline: [{text: 'Built for the job. '}, {text: 'Built for you.', accent: true}],
  heroTagline: 'Hard Hat Staffing Worksite Benefits',
  heroBlurb:
    'Hard Hat Staffing has partnered with Concierge to deliver the full benefits suite for the people who keep America building — medical, dental, vision, life, disability, and critical illness.',
  requiresAccessCode: false,
  accessCodeScreen: {
    headline: [{text: 'Welcome, '}, {text: 'Hard', accent: true}, {text: ' crew.'}],
    blurb:
      "Hard Hat Staffing partners with Concierge to provide quality health benefits — without breaking the bank. That's the Concierge Promise.",
    placeholder: 'e.g., 00000',
  },
  coveragePeriod: 'January 1, 2026 — December 31, 2026',
  supportPhone: '888.820.5687',
  supportText: '918.876.5015',
  supportEmail: 'cs@ctpa.com',
  products: [
    {
      category: 'medical',
      label: 'Medical',
      description:
        'ACA-compliant preventive coverage with virtual care and prescription benefits, plus the AXIS Limited Medical hospital & accident option. Pick one plan or decline.',
      videoUrl: 'https://player.mux.com/1HqLBZyheoGpKf1JumWtBvQneHQx87GBVyHLWp02nC00k',
      planIds: ['preventive-only', 'preventive-plus', 'preventive-bronze'],
      declinable: true,
      benefitRows: [
        {
          category: 'preventive',
          label: 'ACA Preventive Services',
          values: {
            'preventive-only': '100% covered · unlimited',
            'preventive-plus': '100% covered · unlimited',
            'preventive-bronze': '100% covered · unlimited',
          },
        },
        {
          category: 'everyday',
          label: 'Virtual Care 24/7 (Clever)',
          values: {
            'preventive-only': null,
            'preventive-plus': '$0 copay · unlimited',
            'preventive-bronze': '$0 copay · unlimited',
          },
        },
        {
          category: 'everyday',
          label: 'Primary Care Office Visit',
          values: {
            'preventive-only': null,
            'preventive-plus': '$25 copay · 5 visits max',
            'preventive-bronze': '$25 copay · 5 visits max',
          },
        },
        {
          category: 'everyday',
          label: 'Specialist Office Visit',
          values: {
            'preventive-only': null,
            'preventive-plus': '$50 copay · 1 visit max',
            'preventive-bronze': '$50 copay · 5 visits max',
          },
        },
        {
          category: 'everyday',
          label: 'Urgent Care Visit',
          values: {
            'preventive-only': null,
            'preventive-plus': '$50 copay · 2 visits max',
            'preventive-bronze': '$50 copay · 3 visits max',
          },
        },
        {
          category: 'everyday',
          label: 'Additional Physician Visits',
          values: {
            'preventive-only': null,
            'preventive-plus': 'After max — PPO discounts apply',
            'preventive-bronze': 'After max — PPO discounts apply',
          },
        },
        {
          category: 'prescriptions',
          label: 'Prescription Plan',
          values: {
            'preventive-only': 'ACA preventive Rx only',
            'preventive-plus': 'C3Rx formulary · $0 copay (Clever app)',
            'preventive-bronze': 'C3Rx · unlimited ACA Rx $0 copay · formulary $0 copay unlimited',
          },
        },
        {
          category: 'prescriptions',
          label: 'Prescription Discount Program',
          values: {
            'preventive-only': 'Included',
            'preventive-plus': 'Included',
            'preventive-bronze': 'Included',
          },
        },
        {
          category: 'hospital',
          label: 'Accident Coverage',
          values: {
            'preventive-only': null,
            'preventive-plus': null,
            'preventive-bronze': 'Up to $500',
          },
        },
        {
          category: 'hospital',
          label: 'Diagnostic, Lab & X-ray',
          values: {
            'preventive-only': null,
            'preventive-plus': null,
            'preventive-bronze': 'Class I $30 ×2 · Class II $100 ×2 · Class III $100 ×1',
          },
        },
        {
          category: 'hospital',
          label: 'Hospitalization (in-patient)',
          values: {
            'preventive-only': null,
            'preventive-plus': null,
            'preventive-bronze': '$500 day 1 + $250 days 2–30',
          },
        },
        {
          category: 'hospital',
          label: 'Surgery (in/out-patient + anesthesia)',
          values: {
            'preventive-only': null,
            'preventive-plus': null,
            'preventive-bronze': '$500 max',
          },
        },
      ],
    },
    {
      // Additive opt-in offering: renders inline beneath Medical (inlineWith), stacks on top of a
      // preventive pick rather than replacing it, and is not declinable so it never gates Continue.
      category: 'limited-medical',
      label: 'Limited Benefit Medical',
      description:
        'A fixed-indemnity hospital & accident plan underwritten by AXIS that pays set cash benefits. It is not ACA / minimum essential coverage and is chosen separately from the preventive plans above.',
      planIds: ['axis-lm-250'],
      declinable: false,
      inlineWith: 'medical',
      sectionLabel: 'Also available',
      benefitsTableTitle: "What's covered",
      benefitRows: [
        {
          category: 'hospital',
          label: 'Hospital Confinement — Day 1',
          values: {'axis-lm-250': '$250 per day · 1 day'},
        },
        {
          category: 'hospital',
          label: 'Hospital Confinement — Day 7+',
          values: {'axis-lm-250': '$199 per day · 15 days'},
        },
        {
          category: 'hospital',
          label: 'Accident Medical Benefit (per year)',
          values: {'axis-lm-250': '$2,500 max · 80% UCR · $0 deductible per accident'},
        },
        {
          category: 'plan',
          label: 'Coverage type',
          values: {'axis-lm-250': 'Limited benefit medical (fixed indemnity)'},
        },
        {
          category: 'plan',
          label: 'Minimum essential coverage (MEC)',
          values: {'axis-lm-250': 'No — this is not an ACA plan'},
        },
        {
          category: 'plan',
          label: 'Underwriter',
          values: {'axis-lm-250': 'AXIS Insurance Company'},
        },
      ],
    },
    {
      category: 'dental',
      label: 'Dental',
      description: 'Preventive, basic, and major dental services through a single plan.',
      videoUrl: 'https://player.mux.com/00eXT5nl5aRTHFn23JeNIrxuF7NqWxXuOkMafRj2fIlQ',
      planIds: ['dental-1000'],
      declinable: true,
      declineStyle: 'skipped',
    },
    {
      category: 'vision',
      label: 'Vision',
      description: 'Routine eye exams plus generous hardware allowance.',
      videoUrl: 'https://player.mux.com/Vaw1Jm3DsGG8sHD9kFCGj02ayhe1dgL5KHuFuKCSzVlM',
      planIds: ['vision-600'],
      declinable: true,
      declineStyle: 'skipped',
    },
    {
      category: 'critical-illness',
      label: 'Critical Illness',
      description: 'Lump-sum cash benefit if you are diagnosed with a covered condition.',
      planIds: ['critical-illness'],
      declinable: true,
      inlineWith: 'protection',
    },
    {
      category: 'disability',
      label: 'Short-Term Disability',
      description: 'Replaces a portion of your income if you cannot work due to injury or illness.',
      planIds: ['short-term-disability'],
      declinable: true,
      tierSelectable: false,
      inlineWith: 'protection',
    },
    {
      category: 'life',
      label: 'Life Insurance',
      description: '$10,000 group term life with AD&D included. No medical exam.',
      planIds: ['term-life-10k'],
      declinable: true,
      note: 'Add a medical plan to also cover your spouse & dependents on Term Life.',
      inlineWith: 'protection',
    },
  ],
  plans: [
    {
      id: 'preventive-only',
      category: 'medical',
      name: 'Preventive Only',
      tagline: 'ACA preventive services + preventive Rx',
      highlights: [
        'ACA preventive 100%',
        'ACA preventive prescriptions',
        'FirstHealth PPO network',
      ],
      rates: {employee: 17.19, employeeSpouse: 19.53, employeeChildren: 19.01, family: 22.13},
      network: 'FirstHealth PPO',
      sbcUrl: hardhatMedicalSbc,
      accentColor: '#94a3b8',
    },
    {
      id: 'preventive-plus',
      category: 'medical',
      name: 'Preventive Plus',
      tagline: 'ACA preventive + virtual care starter',
      highlights: ['ACA-compliant', 'Virtual care 24/7', '$25 PCP · 5 visits'],
      rates: {employee: 26.31, employeeSpouse: 43.38, employeeChildren: 47.08, family: 65.54},
      network: 'FirstHealth PPO',
      sbcUrl: hardhatMedicalSbc,
      accentColor: '#2563eb',
    },
    {
      id: 'preventive-bronze',
      category: 'medical',
      name: 'Preventive Bronze',
      tagline: 'Adds office visits + accident + hospitalization',
      highlights: ['Specialist + urgent care', 'Accident up to $500', 'Hospitalization day 1+'],
      rates: {employee: 35.54, employeeSpouse: 60.46, employeeChildren: 63, family: 87.46},
      network: 'FirstHealth PPO',
      sbcUrl: hardhatMedicalSbc,
      accentColor: '#b45309',
    },
    {
      id: 'axis-lm-250',
      category: 'limited-medical',
      name: 'AXIS Limited Medical 250',
      tagline: 'Limited-benefit hospital & accident plan — not ACA/MEC',
      highlights: [
        '$250/day hospital confinement',
        '$2,500/yr accident benefit',
        'Lowest weekly cost',
      ],
      rates: {employee: 4.15, employeeSpouse: 5.29, employeeChildren: 7.12, family: 8.99},
      network: 'FirstHealth PPO',
      sbcUrl: hardhatAxisSbc,
      limitedBenefit: true,
      accentColor: '#a78bfa',
    },
    {
      id: 'dental-1000',
      category: 'dental',
      name: 'Dental — Preventive, Basic & Major',
      tagline: '$1,000 annual max · $50 deductible',
      highlights: ['Preventive 100%', 'Basic 80% (90-day wait)', 'Major 50% (180-day wait)'],
      rates: {employee: 7.79, employeeSpouse: 15.17, employeeChildren: 15.17, family: 22.1},
      accentColor: '#0d9488',
    },
    {
      id: 'vision-600',
      category: 'vision',
      name: 'Vision — Routine + Hardware',
      tagline: '$600 annual max · $25 exam copay',
      highlights: ['No deductible', 'Frames + contacts covered', '$0 copay on hardware'],
      rates: {employee: 4.38, employeeSpouse: 7.85, employeeChildren: 7.85, family: 12.92},
      accentColor: '#0891b2',
    },
    {
      id: 'critical-illness',
      category: 'critical-illness',
      name: 'Voluntary Critical Illness',
      tagline: "Lump-sum cash benefit if you're diagnosed with a covered condition",
      highlights: [
        '$5,000 benefit · all conditions at 100%',
        '11 covered conditions including cancer + ALS',
        'Guarantee issue · no medical exam',
      ],
      rates: {employee: 3.74, employeeSpouse: 4.39, employeeChildren: 2.84, family: 4.49},
      accentColor: '#db2777',
      detailRows: [
        {label: 'Benefit amount (all conditions)', value: '$5,000 lump sum at 100%'},
        {label: 'Invasive Cancer', value: '$5,000'},
        {label: 'In Situ Cancer', value: '$5,000'},
        {label: 'Heart Attack', value: '$5,000'},
        {label: 'Coronary Artery Bypass', value: '$5,000'},
        {label: 'Stroke', value: '$5,000'},
        {label: 'Major Organ Transplant', value: '$5,000'},
        {label: 'Multiple Sclerosis', value: '$5,000'},
        {label: "ALS (Lou Gehrig's)", value: '$5,000'},
        {label: "Alzheimer's", value: '$5,000'},
        {label: 'Terminal Illness', value: '$5,000'},
        {label: 'Renal Failure', value: '$5,000'},
        {label: 'Eligibility', value: 'Actively at work · full-time · 30+ hrs/wk'},
        {
          label: 'Medical underwriting',
          value: 'Guarantee issue — not required (waiting periods apply)',
        },
        {label: 'Dependent child eligibility', value: 'Birth through age 26'},
        {label: 'Maximum issue age', value: '70 years old'},
        {label: 'Medicare/Medicaid', value: 'Not eligible if covered by either'},
        {label: 'Underwriter', value: 'AXIS Insurance Company'},
      ],
    },
    {
      id: 'short-term-disability',
      category: 'disability',
      name: 'Short-Term Disability',
      tagline: "Replaces income if you can't work due to injury or illness",
      highlights: [
        '50% of base pay (up to $125/wk)',
        '0-day wait for accidents or hospitalization',
        'Up to 26 weeks of benefits',
      ],
      rates: {employee: 3.74, employeeSpouse: 3.74, employeeChildren: 3.74, family: 3.74},
      accentColor: '#ea580c',
      detailRows: [
        {label: 'Income replacement', value: '50% of base pay'},
        {label: 'Maximum weekly benefit', value: '$125 per week'},
        {
          label: 'Elimination period — accident or hospitalization',
          value: '0 days (benefits begin immediately)',
        },
        {label: 'Elimination period — sickness', value: '7 days'},
        {label: 'Maximum benefit period', value: 'Up to 26 weeks of disability'},
        {label: 'Eligibility', value: 'Employees only — dependent coverage not available'},
        {
          label: 'Pre-existing condition limitation',
          value: '12-month treatment / 12-month limitation',
        },
        {label: 'Underwriter', value: 'AXIS Insurance Company'},
      ],
    },
    {
      id: 'term-life-10k',
      category: 'life',
      name: 'Term Life Insurance',
      tagline: 'Group term life with AD&D — protection for the unexpected',
      highlights: [
        '$10,000 employee benefit',
        'Spouse + dependent coverage with a medical plan',
        'Underwritten by Amalgamated Life',
      ],
      rates: {employee: 1.07, employeeSpouse: 1.48, employeeChildren: 1.48, family: 1.7},
      accentColor: '#7c3aed',
      detailRows: [
        {label: 'Employee benefit amount', value: '$10,000'},
        {label: 'Spouse benefit amount', value: '$5,000'},
        {label: 'Child(ren) benefit amount', value: '$2,000 per child'},
        {
          label: 'Spouse & dependent coverage',
          value: 'Included when the employee is enrolled in a medical plan',
        },
        {label: 'Policy type', value: 'Group term life'},
        {label: 'Medical underwriting', value: 'Not required'},
        {label: 'Coverage age limit', value: 'Benefits discontinue at age 65'},
        {label: 'Dependent child eligibility', value: 'Eligible on the 15th day of life'},
        {label: 'Suicide exclusion', value: '2 years from policy start'},
        {label: 'Underwriter', value: 'Amalgamated Life Insurance Company'},
      ],
    },
  ],
  coverageCards: [
    {
      icon: 'stethoscope',
      color: '#16a34a',
      title: 'Preventive Care',
      bullets: [
        'ACA-compliant preventive care — 100% covered',
        '$0 virtual care + C3Rx prescriptions (Plus & Bronze)',
        'Added office visits, accident & hospital benefits on Bronze',
        'FirstHealth PPO network',
      ],
    },
    {
      icon: 'shield-check',
      color: '#2563eb',
      title: 'Dental',
      bullets: [
        'Preventive services covered 100%',
        'Basic services 80% after 90-day waiting period',
        'Major services 50% after 180-day waiting period',
        '$1,000 annual maximum',
      ],
    },
    {
      icon: 'eye',
      color: '#0d9488',
      title: 'Vision',
      bullets: [
        '$25 copay for routine exam',
        'Frames, lenses, contacts covered after 90-day waiting period',
        'Anti-scratch + AR coatings included',
        '$600 annual combined max (exam + hardware)',
      ],
    },
    {
      icon: 'heart-pulse',
      color: '#db2777',
      title: 'Critical Illness',
      bullets: [
        'Lump-sum cash benefit on diagnosis',
        'Covers heart attack, stroke, cancer, more',
        'Use the money however you need',
        'Spouse + child coverage available',
      ],
    },
    {
      icon: 'hard-hat',
      color: '#b45309',
      title: 'Short-Term Disability',
      bullets: [
        'Replaces 50% of base pay (up to $125/wk)',
        'For injury or illness off the job',
        '0-day wait for accidents or hospitalization',
        'Up to 26 weeks of coverage',
      ],
    },
    {
      icon: 'shield-check',
      color: '#7c3aed',
      title: 'Life Insurance',
      bullets: [
        '$10,000 group term life',
        'AD&D coverage included',
        'No medical exam required',
        'Spouse + dependent coverage with a medical plan',
      ],
    },
  ],
  confirmation: {
    whatHappensNext: [
      {
        title: 'Your benefits are submitted',
        body: "Your 2026 benefits take effect on your plan's effective date — your HR office confirms the exact date and processes your elections for payroll setup. Check with your HR department for your payroll-deduction schedule and any specifics to your location.",
      },
      {
        title: 'Watch for your member ID card',
        body: "You'll receive an electronic ID card by email or text once your coverage starts. You can also print it or pull it up anytime in the <app>Clever Health app</app>.",
      },
      {
        title: 'Set up the Clever Health app',
        body: 'Get the <app>Clever Health app</app> from the Apple App Store or Google Play, then log in with your SSN or Member ID and date of birth to view benefits, access ID cards, print prescriptions, and start a virtual visit.',
      },
    ],
    faq: [
      {
        question: 'When does my coverage start?',
        answer:
          'Your elections take effect for the 2026 plan year. Confirm your exact effective date with your HR department.',
      },
      {
        question: 'When will I get my ID card?',
        answer:
          "You'll receive an electronic ID card by email or text once your coverage starts. You can print copies or access them anytime in the <app>Clever Health app</app> by logging in with your SSN or Member ID and date of birth.",
      },
      {
        question: 'How do I use virtual care?',
        answer:
          'Download the <app>Clever Health app</app>, log in with your SSN or Member ID and date of birth, then tap Telehealth to connect with a licensed physician — calls back within about 14 minutes on average, available 24/7 at a $0 visit cost.',
      },
      {
        question: 'How does my prescription benefit work?',
        answer:
          'Use the app to search medication prices, then fill at any of 70,000+ in-network retail pharmacies. ACA preventive prescriptions are 100% covered; other medications come with a discount card.',
      },
      {
        question: 'Can I change my benefits after enrolling?',
        answer:
          "Once enrolled, you can't make changes until the next Open Enrollment unless you have a qualifying life event (marriage, birth, loss of other coverage, etc.). Contact your HR representative for details on your options.",
      },
      {
        question: 'Who do I contact with questions?',
        answer:
          'Call the Concierge team at <tel>888-820-5687</tel>, text <tel>918-876-5015</tel>, or email <email>eligibility@cleverbenefits.com</email>. For anything policy-specific, check with your HR department.',
      },
    ],
  },
  howToEnroll: HOW_TO_ENROLL_NO_CODE,
};
